import { CHESS_SIDE } from "../constants";
import { diffIn, toDateWithOffset } from "../utils/date-helper";
import { getTrimmedMean } from "../utils/array-helper";
export class TimeCalculator {
    match;
    moves;
    firestoreTimeOffset;
    constructor(match, moves, firestoreTimeOffset = 0) {
        this.match = match;
        this.moves = moves;
        this.firestoreTimeOffset = firestoreTimeOffset;
    }
    /**
     * Calculate durations for both players
     */
    calculateDurations() {
        return {
            white: this.parseNumber(this.match.whiteDuration ?? this.match.duration),
            black: this.parseNumber(this.match.blackDuration ?? this.match.duration),
        };
    }
    /**
     * Calculate increments for both players based on move stats
     */
    calculateIncrements(movesStats) {
        const lastMoveIndex = movesStats.length ? movesStats.length - 1 : -1;
        const blackToStart = this.getBlackToStart();
        return {
            white: movesStats.reduce((total, move, index) => index <= lastMoveIndex &&
                (index + blackToStart + 1) % 2 &&
                move.increment
                ? total + (move.increment || 0)
                : total, 0),
            black: movesStats.reduce((total, move, index) => index <= lastMoveIndex && (index + blackToStart) % 2 && move.increment
                ? total + (move.increment || 0)
                : total, 0),
        };
    }
    /**
     * Calculate total times (duration + increments) for both players
     */
    calculateTotalTimes(durations, increments) {
        return {
            white: (durations.white || 0) + (increments.white || 0),
            black: (durations.black || 0) + (increments.black || 0),
        };
    }
    /**
     * Calculate time offsets for both players
     */
    calculateTimeOffsets() {
        const lastMove = this.moves.at(-1);
        if (!lastMove) {
            return { white: 0, black: 0 };
        }
        return {
            white: this.calculateTimeOffsetByColor(CHESS_SIDE.WHITE),
            black: this.calculateTimeOffsetByColor(CHESS_SIDE.BLACK),
        };
    }
    /**
     * Calculate time left for a specific move
     */
    calculateTimeLeftByMove(moveStats, totalTimes) {
        const moveColor = moveStats ? moveStats.color : CHESS_SIDE.WHITE;
        const timeUsed = moveStats ? moveStats.timeUsed : 0;
        const bufferUsed = moveStats ? moveStats.bufferUsed : 0;
        return {
            timeLeft: totalTimes[moveColor] > 0
                ? totalTimes[moveColor] - timeUsed
                : 0,
            bufferLeft: this.match.buffer_duration - bufferUsed,
        };
    }
    /**
     * Calculate move timestamp with proper ordering
     */
    calculateMoveTimestamp(move, nextMove) {
        // Use createdAt as primary, receivedAt as fallback if createdAt is missing
        let timestamp = move.createdAt || move.receivedAt;
        // If both createdAt and receivedAt exist, use the earlier one
        if (move.createdAt &&
            move.receivedAt &&
            this.isTimestampEarlier(move.receivedAt, move.createdAt)) {
            timestamp = move.receivedAt;
        }
        const nextTimestamp = nextMove
            ? this.calculateMoveTimestamp(nextMove, null)
            : null;
        if (nextTimestamp && this.isTimestampLater(timestamp, nextTimestamp)) {
            if (move.localTimestamp &&
                this.isTimestampEarlier(move.localTimestamp, nextTimestamp)) {
                timestamp = move.localTimestamp;
            }
            else {
                timestamp = nextTimestamp;
            }
        }
        return timestamp;
    }
    /**
     * Calculate send time for a move
     */
    calculateMoveSendTime(move) {
        if (!move?.createdAt)
            return 0;
        return move.createdAt &&
            move.receivedAt &&
            this.isTimestampLater(move.receivedAt, move.createdAt)
            ? this.getTimestampDifference(move.receivedAt, move.createdAt)
            : 0;
    }
    // Private helper methods
    parseNumber(value) {
        const parsed = Number(value);
        return isNaN(parsed) ? null : parsed;
    }
    getBlackToStart() {
        return this.match.initial_fen && this.match.initial_fen.includes(" b ")
            ? 1
            : 0;
    }
    calculateTimeOffsetByColor(color) {
        const offsetsByColor = this.moves
            .filter((move) => move.color === color)
            .map((move) => {
            return diffIn(move.createdAt || toDateWithOffset(this.firestoreTimeOffset), move.localTimestamp, "second");
        });
        return getTrimmedMean(offsetsByColor, 0.25) || 0;
    }
    isTimestampEarlier(timestamp1, timestamp2) {
        // Handle Firebase Timestamp comparison
        if (timestamp1?.toMillis && timestamp2?.toMillis) {
            return timestamp1.toMillis() < timestamp2.toMillis();
        }
        // Fallback for other timestamp types
        return timestamp1 < timestamp2;
    }
    isTimestampLater(timestamp1, timestamp2) {
        // Handle Firebase Timestamp comparison
        if (timestamp1?.toMillis && timestamp2?.toMillis) {
            return timestamp1.toMillis() > timestamp2.toMillis();
        }
        // Fallback for other timestamp types
        return timestamp1 > timestamp2;
    }
    getTimestampDifference(timestamp1, timestamp2) {
        // Handle Firebase Timestamp subtraction - convert to seconds
        if (timestamp1?.toMillis && timestamp2?.toMillis) {
            return (timestamp1.toMillis() - timestamp2.toMillis()) / 1000;
        }
        // Fallback for other timestamp types - convert to seconds
        return (Number(timestamp1) - Number(timestamp2)) / 1000;
    }
}
